import { Button, Modal, SegmentedControl, useToast } from "@design";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { z } from "zod";
import { schemas } from "@/api";
import { useAppForm } from "@/components/form/form";
import { Text } from "@/components/Text";
import { ImportProgress } from "./ImportProgress";
import { ImportFileField, ImportSpaceFileField } from "./ImportSourceFields";
import { isRunning, useDeleteImport, useImport, useImportErrorMessage, useStartImport } from "./useImports";

type Source = "upload" | "paste" | "spaceFile";

// Text limits come from the generated KGImportText schema (the backend's rules).
const text = schemas.zKgImportText.shape;

function importSchema(m: { file: string; text: string; spaceFile: string }) {
  return z
    .object({
      source: z.enum(["upload", "paste", "spaceFile"]),
      file: z.instanceof(File).nullable(),
      title: text.title.unwrap().unwrap(),
      text: z.string(),
      spaceFileUuid: z.string(),
    })
    .superRefine((v, ctx) => {
      if (v.source === "upload" && !v.file) ctx.addIssue({ code: "custom", path: ["file"], message: m.file });
      if (v.source === "paste" && (!v.text.trim() || !text.text.safeParse(v.text.trim()).success))
        ctx.addIssue({ code: "custom", path: ["text"], message: m.text });
      if (v.source === "spaceFile" && !v.spaceFileUuid)
        ctx.addIssue({ code: "custom", path: ["spaceFileUuid"], message: m.spaceFile });
    });
}

/**
 * Import a document into the knowledge graph (docs/design/knowledge-graph/KNOWLEDGE_GRAPH_DESIGN.md §6.4):
 * upload a file, paste text, or pick one of the space's files. Facts found go to
 * the caller's inbox for review; this dialog shows progress, then links there.
 * Mount only while open.
 */
export function ImportKnowledgeModal({ spaceUuid, onClose }: Readonly<{ spaceUuid: string; onClose: () => void }>) {
  const { t } = useTranslation("knowledge");
  const [started, setStarted] = useState<{ uuid: string; alreadyImported: boolean } | null>(null);
  if (started) {
    return <ImportRunning importUuid={started.uuid} alreadyImported={started.alreadyImported} onClose={onClose} />;
  }
  return (
    <ImportForm
      spaceUuid={spaceUuid}
      onClose={onClose}
      onStarted={(uuid, alreadyImported) => setStarted({ uuid, alreadyImported })}
      title={t("import.title")}
    />
  );
}

function ImportForm({
  spaceUuid,
  onClose,
  onStarted,
  title,
}: Readonly<{
  spaceUuid: string;
  onClose: () => void;
  onStarted: (uuid: string, alreadyImported: boolean) => void;
  title: string;
}>) {
  const { t } = useTranslation("knowledge");
  const toast = useToast();
  const message = useImportErrorMessage();
  const start = useStartImport();
  const path = { space_uuid: spaceUuid };

  const form = useAppForm({
    defaultValues: {
      source: "upload" as Source,
      file: null as File | null,
      title: "",
      text: "",
      spaceFileUuid: "",
    },
    validators: {
      onSubmit: importSchema({
        file: t("import.fileRequired"),
        text: t("import.textRequired"),
        spaceFile: t("import.spaceFileRequired"),
      }),
    },
    onSubmit: async ({ value }) => {
      try {
        const res =
          value.source === "upload" && value.file
            ? await start.upload.mutateAsync({ path, body: { file: value.file }, throwOnError: true })
            : await start.create.mutateAsync({
                path,
                body:
                  value.source === "paste"
                    ? { title: value.title.trim() || null, text: value.text.trim() }
                    : { spaceFileUuid: value.spaceFileUuid },
                throwOnError: true,
              });
        onStarted(res.data.import.uuid, res.data.alreadyImported);
      } catch (error) {
        toast.error(message(error, t("import.startFailed")));
      }
    },
  });

  return (
    <Modal
      open
      onClose={onClose}
      title={title}
      actions={
        <>
          <Button variant="secondary" onClick={onClose}>
            {t("import.cancel")}
          </Button>
          <form.Subscribe selector={(s) => s.isSubmitting}>
            {(submitting) => (
              <Button variant="primary" loading={submitting} onClick={() => void form.handleSubmit()}>
                {t("import.start")}
              </Button>
            )}
          </form.Subscribe>
        </>
      }
    >
      <form
        aria-label={title}
        className="flex flex-col gap-4"
        onSubmit={(e) => {
          e.preventDefault();
          void form.handleSubmit();
        }}
      >
        <Text tone="secondary">{t("import.intro")}</Text>
        <form.Field name="source">
          {(field) => (
            <SegmentedControl<Source>
              aria-label={t("import.source")}
              fullWidth
              value={field.state.value}
              onChange={(v) => field.handleChange(v)}
              options={[
                { value: "upload", label: t("import.sources.upload") },
                { value: "paste", label: t("import.sources.paste") },
                { value: "spaceFile", label: t("import.sources.spaceFile") },
              ]}
            />
          )}
        </form.Field>
        <form.Subscribe selector={(s) => s.values.source}>
          {(source) => (
            <>
              {source === "upload" && (
                <form.Field name="file">{(field) => <ImportFileField field={field} />}</form.Field>
              )}
              {source === "paste" && (
                <>
                  <form.AppField name="title">
                    {(field) => (
                      <field.TextField label={t("import.pasteTitle")} placeholder={t("import.pasteTitleHint")} />
                    )}
                  </form.AppField>
                  <form.AppField name="text">
                    {(field) => (
                      <field.TextareaField
                        label={t("import.pasteText")}
                        placeholder={t("import.pasteTextHint")}
                        rows={10}
                        required
                      />
                    )}
                  </form.AppField>
                </>
              )}
              {source === "spaceFile" && (
                <form.Field name="spaceFileUuid">
                  {(field) => <ImportSpaceFileField spaceUuid={spaceUuid} field={field} />}
                </form.Field>
              )}
            </>
          )}
        </form.Subscribe>
      </form>
    </Modal>
  );
}

/** Progress of a started import, then the way to the inbox. */
function ImportRunning({
  importUuid,
  alreadyImported,
  onClose,
}: Readonly<{ importUuid: string; alreadyImported: boolean; onClose: () => void }>) {
  const { t } = useTranslation("knowledge");
  const navigate = useNavigate();
  const row = useImport(importUuid);
  const stop = useDeleteImport();
  const running = !row.data || isRunning(row.data);
  const reviewable = row.data?.status === "done" && row.data.pendingCount > 0;

  return (
    <Modal
      open
      onClose={onClose}
      title={t("import.title")}
      actions={
        <>
          {running ? (
            <Button
              variant="secondary"
              loading={stop.isPending}
              onClick={() => stop.mutate({ path: { import_uuid: importUuid } })}
            >
              {t("import.stop")}
            </Button>
          ) : (
            <Button variant="secondary" onClick={onClose}>
              {t("import.close")}
            </Button>
          )}
          {reviewable && row.data && (
            <Button variant="primary" onClick={() => navigate(`/knowledge/inbox?space=${row.data?.spaceUuid}`)}>
              {t("import.review")}
            </Button>
          )}
        </>
      }
    >
      <ImportProgress row={row.data} alreadyImported={alreadyImported} loadFailed={row.isError} />
    </Modal>
  );
}
